import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import i18n from "@/i18n";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  setAccessToken,
  clearAuthState,
} from "@/lib/safeStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Defer the initial /auth/me call into a microtask so React doesn't
    // see a sync setState during the effect (avoids the
    // react-hooks/set-state-in-effect lint rule while preserving the
    // "check auth on mount" semantics).
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) checkAuth();
    });
    return () => { cancelled = true; };
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      // iter 64: detect the email_not_verified gate so the UI can show a
      // dedicated CTA "Re-send confirmation link" instead of the generic
      // "wrong password" message. Backend returns 403 with a structured
      // detail object on this path.
      const detail = e.response?.data?.detail;
      if (
        e.response?.status === 403 &&
        typeof detail === "object" &&
        detail?.code === "email_not_verified"
      ) {
        return {
          ok: false,
          email_not_verified: true,
          email: detail.email || email,
          error: detail.message || i18n.t("auth.err_email_not_verified"),
        };
      }
      const detailMsg = formatApiErrorDetail(detail);
      // For 401 (invalid credentials) the backend returns a Romanian-only
      // `detail` string, which would override the i18n fallback. Always use
      // the translated string for this code so EN users see EN errors.
      if (e.response?.status === 401) {
        return { ok: false, error: i18n.t("auth.err_invalid_credentials") };
      }
      const fallback = e.response
        ? i18n.t("auth.err_invalid_credentials")
        : i18n.t("auth.err_connection_slow");
      return { ok: false, error: detailMsg || fallback };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      // iter 72: forward the active UI language so the verification email
      // arrives in the same language the visitor used during sign-up.
      const lang = (typeof window !== "undefined" && window.localStorage?.getItem("i18nextLng")) || "ro";
      const language = lang.toLowerCase().startsWith("en") ? "en" : "ro";
      const { data } = await api.post("/auth/register", { ...payload, language });
      // iter 64: when backend gates on email verification, it returns
      // {ok: true, email_verification_required: true, email, message} and
      // does NOT include access_token. The caller renders a "check inbox"
      // screen instead of auto-logging the user in.
      if (data.email_verification_required) {
        return { ok: true, payload: data };
      }
      if (data.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      return { ok: true, user: data.user, payload: data };
    } catch (e) {
      const detailMsg = formatApiErrorDetail(e.response?.data?.detail);
      // For 409 the backend returns Romanian-only detail ("Acest email este
      // deja înregistrat"). Override with the i18n string so EN users see EN.
      if (e.response?.status === 409) {
        return { ok: false, error: i18n.t("auth.err_email_taken") };
      }
      const fallback = e.response
        ? i18n.t("register.err_register_failed")
        : i18n.t("auth.err_connection_slow");
      return { ok: false, error: detailMsg || fallback };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout endpoint is best-effort; client-side cleanup below always runs.
      // We log so silent token-revocation failures still surface in dev tools.
      // eslint-disable-next-line no-console
      console.warn("Logout endpoint failed (client state still cleared):", e?.message || e);
    }
    clearAuthState();
    setUser(false);
  }, []);

  // Memoize the context value so consumers re-render only when something
  // actually changes — not on every AuthProvider render (e.g. while toasts
  // bubble React updates up the tree).
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: checkAuth }),
    [user, loading, login, register, logout, checkAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
